import React, { useState } from 'react';
import { Modal, Form, Input, Button, Space, Tag, Select, Avatar } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUser } from '@fortawesome/pro-solid-svg-icons';
import { createPlannerPage } from '../../../services/tasks';
import { getUsers, UserWithStats } from '../../../services/users';

interface CreatePlannerPageModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePlannerPageModal: React.FC<CreatePlannerPageModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [statuses, setStatuses] = useState<string[]>(['ideas', 'design', 'development', 'testing', 'deployed']);
  const [newStatus, setNewStatus] = useState('');
  const [swimlanes, setSwimlanes] = useState<Array<{ type: string; value: string }>>([]);
  const [swimlaneType, setSwimlaneType] = useState<string>('priority');
  const [swimlaneValue, setSwimlaneValue] = useState<string>('');
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingStatusIdx, setEditingStatusIdx] = useState<number | null>(null);
  const [editingSwimlaneIdx, setEditingSwimlaneIdx] = useState<number | null>(null);

  React.useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddStatus = () => {
    const toAdd = newStatus
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && !statuses.includes(s));
    if (toAdd.length === 0) {
      setNewStatus('');
      return;
    }
    setStatuses([...statuses, ...toAdd]);
    setNewStatus('');
  };

  const handleRemoveStatus = (status: string) => {
    setStatuses(statuses.filter((s) => s !== status));
  };

  const handleAddSwimlane = () => {
    const toAdd = swimlaneValue
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && !swimlanes.some((sl) => sl.type === swimlaneType && sl.value === s));
    if (toAdd.length === 0) {
      setSwimlaneValue('');
      return;
    }
    setSwimlanes([...swimlanes, ...toAdd.map((value) => ({ type: swimlaneType, value }))]);
    setSwimlaneValue('');
  };

  const handleReorderStatus = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const list = [...statuses];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    setStatuses(list);
  };

  const handleReorderSwimlane = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const list = [...swimlanes];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    setSwimlanes(list);
  };

  const handleRemoveSwimlane = (index: number) => {
    setSwimlanes(swimlanes.filter((_, i) => i !== index));
    setEditingSwimlaneIdx(null);
  };

  const handleSwimlaneValueRename = (index: number, newValue: string) => {
    setEditingSwimlaneIdx(null);
    const trimmed = newValue.trim();
    if (!trimmed) return;
    const list = swimlanes.map((sl, i) => (i === index ? { ...sl, value: trimmed } : sl));
    setSwimlanes(list);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await createPlannerPage({
        name: values.name,
        description: values.description,
        statuses: statuses,
        swimlanes: swimlanes.length > 0 ? swimlanes : undefined,
        memberIds: values.members,
      });
      form.resetFields();
      setStatuses(['ideas', 'design', 'development', 'testing', 'deployed']);
      setSwimlanes([]);
      setSwimlaneValue('');
      onSuccess();
    } catch (error) {
      console.error('Failed to create planner page:', error);
    }
  };

  return (
    <Modal
      title="Create New Planner Page"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Create
        </Button>,
      ]}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Page Name"
          rules={[{ required: true, message: 'Please enter a page name' }]}
        >
          <Input placeholder="e.g., Product Development, Marketing Tasks" />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <Input.TextArea rows={3} placeholder="Optional description for this planner page" />
        </Form.Item>

        <Form.Item name="members" label="Share with Members">
          <Select
            mode="multiple"
            placeholder="Select members to share with"
            loading={loadingUsers}
            optionFilterProp="children"
          >
            {users.map((user) => (
              <Select.Option key={user.id} value={user.id}>
                <Space>
                  <Avatar size="small" src={user.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                  {user.name}
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Status Columns">
          <div style={{ marginBottom: '8px' }}>
            <Space wrap size="small">
              {statuses.map((status, idx) => (
                editingStatusIdx === idx ? (
                  <Input
                    key={status}
                    size="small"
                    autoFocus
                    defaultValue={status}
                    style={{ width: 120 }}
                    onBlur={(e) => {
                      const v = e.target.value.trim().toLowerCase();
                      if (v && v !== status && !statuses.includes(v)) {
                        setStatuses(statuses.map((s, i) => (i === idx ? v : s)));
                      }
                      setEditingStatusIdx(null);
                    }}
                    onPressEnter={(e) => {
                      e.preventDefault();
                      const v = (e.target as HTMLInputElement).value.trim().toLowerCase();
                      if (v && v !== status && !statuses.includes(v)) {
                        setStatuses(statuses.map((s, i) => (i === idx ? v : s)));
                      }
                      setEditingStatusIdx(null);
                    }}
                  />
                ) : (
                <Tag
                  key={status}
                  closable
                  onClose={() => handleRemoveStatus(status)}
                  onClick={() => setEditingStatusIdx(idx)}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', String(idx));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                    if (!Number.isNaN(fromIdx)) handleReorderStatus(fromIdx, idx);
                  }}
                  style={{ fontSize: '14px', padding: '4px 8px', cursor: 'grab' }}
                >
                  {status}
                </Tag>
                )
              ))}
            </Space>
          </div>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Add status column (comma-separated for multiple)"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              onPressEnter={handleAddStatus}
            />
            <Button icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleAddStatus}>
              Add
            </Button>
          </Space.Compact>
          <p style={{ color: '#BFBFBF', fontSize: '12px', marginTop: '8px', marginBottom: 0 }}>
            Customize the columns for this planner page. You can add, remove, or reorder them.
          </p>
        </Form.Item>

        <Form.Item label="Swimlanes (Optional)">
          <p style={{ color: '#BFBFBF', fontSize: '12px', marginBottom: '8px' }}>
            Group tasks horizontally by priority, assignee, epic, or team
          </p>
          <div style={{ marginBottom: '8px' }}>
            <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
              <Select
                value={swimlaneType}
                onChange={setSwimlaneType}
                style={{ width: '40%' }}
              >
                <Select.Option value="priority">By Priority</Select.Option>
                <Select.Option value="assignee">By Assignee</Select.Option>
                <Select.Option value="epic">By Epic/Feature</Select.Option>
                <Select.Option value="team">By Team</Select.Option>
              </Select>
              <Input
                placeholder={swimlaneType === 'priority' ? 'e.g., High, Medium, Low (comma-separated for multiple)' : 
                             swimlaneType === 'assignee' ? 'e.g., John Doe, Jane Smith' :
                             swimlaneType === 'epic' ? 'e.g., User Auth, Dashboard' :
                             'e.g., Frontend, Backend, Design'}
                value={swimlaneValue}
                onChange={(e) => setSwimlaneValue(e.target.value)}
                onPressEnter={handleAddSwimlane}
                style={{ width: '60%' }}
              />
            </Space.Compact>
            <Button 
              icon={<FontAwesomeIcon icon={faPlus} />} 
              onClick={handleAddSwimlane}
              disabled={!swimlaneValue.trim()}
            >
              Add Swimlane
            </Button>
          </div>
          {swimlanes.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <Space wrap size="small">
                {swimlanes.map((swimlane, idx) => (
                  editingSwimlaneIdx === idx ? (
                    <Input
                      key={idx}
                      size="small"
                      autoFocus
                      defaultValue={swimlane.value}
                      style={{ width: 120 }}
                      onBlur={(e) => handleSwimlaneValueRename(idx, e.target.value)}
                      onPressEnter={(e) => {
                        e.preventDefault();
                        handleSwimlaneValueRename(idx, (e.target as HTMLInputElement).value);
                      }}
                    />
                  ) : (
                  <Tag
                    key={idx}
                    closable
                    onClose={() => handleRemoveSwimlane(idx)}
                    onClick={() => setEditingSwimlaneIdx(idx)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', String(idx));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (!Number.isNaN(fromIdx)) handleReorderSwimlane(fromIdx, idx);
                    }}
                    style={{ fontSize: '14px', padding: '4px 8px', cursor: 'pointer' }}
                  >
                    {swimlane.type}: {swimlane.value}
                  </Tag>
                  )
                ))}
              </Space>
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreatePlannerPageModal;

